import React from 'react';
import { Box, Container, Typography, Link, Grid, alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useCustomerPortal } from '../../contexts/CustomerPortalContext';

// Textos bilingües INLINE (patrón del manual): el pie no debe depender de claves
// en los JSON de i18n —que otros agentes tocan a la vez— y así es de verdad
// es/en en vez de quedarse en un idioma con el fallback.
const COPY = {
  es: {
    tagline: 'Empleados de IA para tu negocio, listos para trabajar.',
    product: 'Producto',
    myAccount: 'Mi cuenta',
    pricing: 'Planes y créditos',
    help: 'Ayuda',
    support: 'Soporte',
    rights: 'Todos los derechos reservados.',
  },
  en: {
    tagline: 'AI employees for your business, ready to work.',
    product: 'Product',
    myAccount: 'My account',
    pricing: 'Plans & credits',
    help: 'Help',
    support: 'Support',
    rights: 'All rights reserved.',
  },
};

const CustomerFooter: React.FC = () => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const t = i18n.language?.startsWith('en') ? COPY.en : COPY.es;
  // El pie sale en TODAS las páginas del portal (también en «Mi cuenta»), así
  // que su enlace a Planes era la última puerta abierta a /customer/plans para
  // un cliente gestionado: se oculta igual que el resto de la superficie de
  // pago. Mientras se resuelve la cuenta (loading) también se oculta.
  const { managedByReseller, loading } = useCustomerPortal();
  const hidePricing = loading || !!managedByReseller;

  const linkSx = { mb: 1 } as const;

  return (
    <Box
      component="footer"
      sx={{
        py: 6,
        px: 2,
        mt: 'auto',
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(20px)',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
              WAI
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t.tagline}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              {t.product}
            </Typography>
            <Link component={RouterLink} to="/customer/account" color="text.secondary" display="block" sx={linkSx}>
              {t.myAccount}
            </Link>
            {!hidePricing && (
              <Link component={RouterLink} to="/customer/plans" color="text.secondary" display="block" sx={linkSx}>
                {t.pricing}
              </Link>
            )}
            <Link component={RouterLink} to="/customer/help" color="text.secondary" display="block" sx={linkSx}>
              {t.help}
            </Link>
            <Link component={RouterLink} to="/customer/support" color="text.secondary" display="block">
              {t.support}
            </Link>
          </Grid>
        </Grid>
        <Box mt={5}>
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} WAI. {t.rights}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default CustomerFooter;
